import { ProtobufClient } from 'api/protobuf/protobuf-client';
import { PackageProviderName } from 'data/ppoints/constants';
import { goBackendApi } from 'hooks/api/httpApi';
import {
    MarketListResponse,
    MarketListResponseSchema,
    MarketShippingRates,
} from '../../../../../pb/responses/v1/market_list_response_pb';

export const MARKET_SHIPPING_RATES_PATH = '/api/v1/markets';

/** Markets per page; the remaining pages are followed through `endCursor`. */
const MARKETS_PAGE_SIZE = 10;

export interface GetAllMarketShippingRatesOptions {
    /**
     * Also return rates the backend can't vouch for any more. Sent as the `maybe_invalid` query param on every page;
     * omitted when false.
     */
    maybeInvalid?: boolean;
}

/**
 * Every market's shipping rates for the countries `provider` covers, across all pages. The first page
 * is requested without `after`; each next one with the previous page's `endCursor`, for as long as
 * `hasNextPage` says there are more markets. A 404 means there are none, which is an empty list
 * rather than an error.
 *
 * Each entry names the market it was read of (`marketId`), which is how its definitions are matched
 * back to the markets `useMappingCountryMarkets` returns.
 */
export const getAllMarketShippingRates = async (
    provider: PackageProviderName,
    { maybeInvalid }: GetAllMarketShippingRatesOptions = {}
): Promise<MarketShippingRates[]> => {
    const client = new ProtobufClient(goBackendApi());
    const shippingRates: MarketShippingRates[] = [];
    let after: string | undefined;

    do {
        const page: MarketListResponse | undefined = await client
            .getRequest(MarketListResponseSchema, MARKET_SHIPPING_RATES_PATH, {
                provider,
                first: MARKETS_PAGE_SIZE,
                ...(after && { after }),
                ...(maybeInvalid && { maybe_invalid: true }),
            })
            .catch((error) => {
                if (error.status === 404) return undefined;
                throw error;
            });

        shippingRates.push(...(page?.shippingRates ?? []));
        after = page?.hasNextPage && page.endCursor ? page.endCursor : undefined;
    } while (after);

    return shippingRates;
};
